import { Laptop } from '@mui/icons-material';
import { Breakpoint, useTheme } from '@mui/material/styles';
import { useEffect, useState } from 'react';

export const fixedSizes = {
  profile: {
    avatarStatic: {
      mobile: 80,
      tablet: 100,
      laptop: 84,
    },
  },
  section: {
    sectionIcon: {
      mobile: 28,
      tablet: 28,
      laptop: 60,
    },
  },
};

export const defaultSpacingPresets = {
  general: {
    maxBlockWidth: 1440,
    paddingX: 35,
    paddingY: 8,
  },
  sp: {
    s160: 16,
    s150: 15,
    s120: 12,
    s100: 10,
    s90: 9,
    s80: 8,
    s70: 7,
    s75: 7.5,
    s60: 6,
    s50: 5,
    s40: 4,
    s30: 3,
    s25: 2.5,
    s20: 2,
    s15: 1.5,
    s10: 1,
  },
  modals: {
    w: 600,
    menuModalW: 430,
  },
  search: {
    firstLvlWidth: 15,
    secondLvlWidth: 57,
  },
  header: {
    height: 80,
    logoW: 233,
    logoH: 61,
  },
  loginPage: {
    loginForm: {
      w: 530,
    },
  },
  mainPage: {
    searchWrapper: {
      paddingY: 20,
    },
  },
  mainMenu: {
    avatar: {
      w: 48,
      h: 48,
    },
  },
  profilePage: {
    profileCard: {
      w: 380,
      h: 340,
    },
    profileEdit: {
      avatarFormGap: 45,
      avatarW: 300,
      avatarH: 300,
      formWidth: 340,
    },
  },
  fileGrid: {
    filePreviewWidth: 110,
  },
  controls: {
    checkbox: {
      w: 19,
      h: 19,
    },
    // Это эмперический замер path внутри чекбоксовой svg, нужно для расчета отступов
    checkboxInner: {
      w: 14.25,
      h: 14.25,
    },
    switch: {
      w: 36,
      h: 20,
    },
    sectionWidget: {
      h: {
        mobile: 104,
        laptop: 164,
      },
    },
  },
  icons: {
    '6x6': {
      w: 6,
      h: 6,
    },
    '8x8': {
      w: 8,
      h: 8,
    },
    '10x10': {
      w: 10,
      h: 10,
    },
    '12x12': {
      w: 12,
      h: 12,
    },
    '14x14': {
      w: 14,
      h: 14,
    },
    '16x16': {
      w: 16,
      h: 16,
    },
    '18x18': {
      w: 18,
      h: 18,
    },
    '18x22': {
      w: 18,
      h: 22,
    },
    '23x22': {
      w: 23,
      h: 22,
    },
    '17x22': {
      w: 17,
      h: 22,
    },
    '20x20': {
      w: 20,
      h: 20,
    },
    '20x22': {
      w: 20,
      h: 22,
    },
    '22x22': {
      w: 22,
      h: 22,
    },
    '24x24': {
      w: 24,
      h: 24,
    },
    '28x28': {
      w: 28,
      h: 28,
    },
    '27x16': {
      w: 27,
      h: 16,
    },
    '32x32': {
      w: 32,
      h: 32,
    },
    '38x38': {
      w: 38,
      h: 38,
    },
    '40x22': {
      w: 40,
      h: 22,
    },
    '40x40': {
      w: 40,
      h: 40,
    },
    '42x42': {
      w: 42,
      h: 42,
    },
    '48x48': {
      w: 48,
      h: 48,
    },
    '64x32': {
      w: 64,
      h: 32,
    },
    '80x80': {
      w: 80,
      h: 80,
    },
    '92x92': {
      w: 92,
      h: 92,
    },
  },
};

export type IconsSizes = keyof (typeof defaultSpacingPresets)['icons'];

const generateProportionalPreset = (
  proportion: number,
  iconsProportion: number
) => {
  return {
    general: {
      maxBlockWidth: defaultSpacingPresets.general.maxBlockWidth,
      paddingX: defaultSpacingPresets.general.paddingX / proportion,
      paddingY: defaultSpacingPresets.general.paddingY / proportion,
    },
    sp: {
      s160: defaultSpacingPresets.sp.s160 / proportion,
      s150: defaultSpacingPresets.sp.s150 / proportion,
      s120: defaultSpacingPresets.sp.s120 / proportion,
      s100: defaultSpacingPresets.sp.s100 / proportion,
      s90: defaultSpacingPresets.sp.s90 / proportion,
      s80: defaultSpacingPresets.sp.s80 / proportion,
      s70: defaultSpacingPresets.sp.s70 / proportion,
      s75: defaultSpacingPresets.sp.s75 / proportion,
      s60: defaultSpacingPresets.sp.s60 / proportion,
      s50: defaultSpacingPresets.sp.s50 / proportion,
      s40: defaultSpacingPresets.sp.s40 / proportion,
      s30: defaultSpacingPresets.sp.s30 / proportion,
      s25: defaultSpacingPresets.sp.s25 / proportion,
      s20: defaultSpacingPresets.sp.s20 / proportion,
      s15: defaultSpacingPresets.sp.s15 / proportion,
      s10: defaultSpacingPresets.sp.s10 / proportion,
    },
    header: {
      height: defaultSpacingPresets.header.height / proportion,
      logoW: defaultSpacingPresets.header.logoW / proportion,
      logoH: defaultSpacingPresets.header.logoH / proportion,
    },
    search: {
      firstLvlWidth: defaultSpacingPresets.search.firstLvlWidth / proportion,
      secondLvlWidth: defaultSpacingPresets.search.secondLvlWidth / proportion,
    },
    loginPage: {
      loginForm: {
        w: defaultSpacingPresets.loginPage.loginForm.w / proportion,
      },
    },
    mainPage: {
      searchWrapper: {
        paddingY:
          defaultSpacingPresets.mainPage.searchWrapper.paddingY / proportion,
      },
    },
    mainMenu: {
      avatar: {
        w: defaultSpacingPresets.mainMenu.avatar.w / proportion,
        h: defaultSpacingPresets.mainMenu.avatar.h / proportion,
      },
    },
    profilePage: {
      profileCard: {
        w: defaultSpacingPresets.profilePage.profileCard.w / proportion,
        h: defaultSpacingPresets.profilePage.profileCard.h / proportion,
      },
      profileEdit: {
        avatarW:
          defaultSpacingPresets.profilePage.profileEdit.avatarW / proportion,
        avatarH:
          defaultSpacingPresets.profilePage.profileEdit.avatarH / proportion,
        avatarFormGap:
          defaultSpacingPresets.profilePage.profileEdit.avatarFormGap /
          proportion,
        formWidth:
          defaultSpacingPresets.profilePage.profileEdit.formWidth / proportion,
      },
    },
    fileGrid: {
      filePreviewWidth:
        defaultSpacingPresets.fileGrid.filePreviewWidth / proportion,
    },
    modals: {
      w: defaultSpacingPresets.modals.w / proportion,
      menuModalW: defaultSpacingPresets.modals.menuModalW / proportion,
    },
    controls: {
      checkbox: {
        w: defaultSpacingPresets.controls.checkbox.w / proportion,
        h: defaultSpacingPresets.controls.checkbox.h / proportion,
      },
      checkboxInner: {
        w: defaultSpacingPresets.controls.checkboxInner.w / proportion,
        h: defaultSpacingPresets.controls.checkboxInner.h / proportion,
      },
      switch: {
        w: defaultSpacingPresets.controls.switch.w / proportion,
        h: defaultSpacingPresets.controls.switch.h / proportion,
      },
      sectionWidget: {
        h: {
          mobile: 104,
          laptop: 164,
        },
      },
    },
    icons: {
      '6x6': {
        w: defaultSpacingPresets.icons['6x6'].w / iconsProportion,
        h: defaultSpacingPresets.icons['6x6'].h / iconsProportion,
      },
      '8x8': {
        w: defaultSpacingPresets.icons['8x8'].w / iconsProportion,
        h: defaultSpacingPresets.icons['8x8'].h / iconsProportion,
      },
      '12x12': {
        w: defaultSpacingPresets.icons['12x12'].w / iconsProportion,
        h: defaultSpacingPresets.icons['12x12'].h / iconsProportion,
      },
      '14x14': {
        w: defaultSpacingPresets.icons['14x14'].w / iconsProportion,
        h: defaultSpacingPresets.icons['14x14'].h / iconsProportion,
      },
      '10x10': {
        w: defaultSpacingPresets.icons['10x10'].w / iconsProportion,
        h: defaultSpacingPresets.icons['10x10'].h / iconsProportion,
      },
      '16x16': {
        w: defaultSpacingPresets.icons['16x16'].w / iconsProportion,
        h: defaultSpacingPresets.icons['16x16'].h / iconsProportion,
      },
      '18x18': {
        w: defaultSpacingPresets.icons['18x18'].w / iconsProportion,
        h: defaultSpacingPresets.icons['18x18'].h / iconsProportion,
      },
      '18x22': {
        w: defaultSpacingPresets.icons['18x22'].w / iconsProportion,
        h: defaultSpacingPresets.icons['18x22'].h / iconsProportion,
      },
      '23x22': {
        w: defaultSpacingPresets.icons['23x22'].w / iconsProportion,
        h: defaultSpacingPresets.icons['23x22'].h / iconsProportion,
      },
      '17x22': {
        w: defaultSpacingPresets.icons['17x22'].w / iconsProportion,
        h: defaultSpacingPresets.icons['17x22'].h / iconsProportion,
      },
      '20x20': {
        w: defaultSpacingPresets.icons['20x20'].w / iconsProportion,
        h: defaultSpacingPresets.icons['20x20'].h / iconsProportion,
      },
      '20x22': {
        w: defaultSpacingPresets.icons['20x22'].w / iconsProportion,
        h: defaultSpacingPresets.icons['20x22'].h / iconsProportion,
      },
      '22x22': {
        w: defaultSpacingPresets.icons['22x22'].w / iconsProportion,
        h: defaultSpacingPresets.icons['22x22'].h / iconsProportion,
      },
      '24x24': {
        w: defaultSpacingPresets.icons['24x24'].w / iconsProportion,
        h: defaultSpacingPresets.icons['24x24'].h / iconsProportion,
      },
      '28x28': {
        w: defaultSpacingPresets.icons['28x28'].w / iconsProportion,
        h: defaultSpacingPresets.icons['28x28'].h / iconsProportion,
      },
      '27x16': {
        w: defaultSpacingPresets.icons['27x16'].w / iconsProportion,
        h: defaultSpacingPresets.icons['27x16'].h / iconsProportion,
      },
      '32x32': {
        w: defaultSpacingPresets.icons['32x32'].w / iconsProportion,
        h: defaultSpacingPresets.icons['32x32'].h / iconsProportion,
      },
      '38x38': {
        w: defaultSpacingPresets.icons['38x38'].w / iconsProportion,
        h: defaultSpacingPresets.icons['38x38'].h / iconsProportion,
      },
      '40x22': {
        w: defaultSpacingPresets.icons['40x22'].w / iconsProportion,
        h: defaultSpacingPresets.icons['40x22'].h / iconsProportion,
      },
      '40x40': {
        w: defaultSpacingPresets.icons['40x40'].w / iconsProportion,
        h: defaultSpacingPresets.icons['40x40'].h / iconsProportion,
      },
      '42x42': {
        w: defaultSpacingPresets.icons['42x42'].w / iconsProportion,
        h: defaultSpacingPresets.icons['42x42'].h / iconsProportion,
      },
      '48x48': {
        w: defaultSpacingPresets.icons['48x48'].w / iconsProportion,
        h: defaultSpacingPresets.icons['48x48'].h / iconsProportion,
      },
      '64x32': {
        w: defaultSpacingPresets.icons['64x32'].w / iconsProportion,
        h: defaultSpacingPresets.icons['64x32'].h / iconsProportion,
      },
      '80x80': {
        w: defaultSpacingPresets.icons['80x80'].w / iconsProportion,
        h: defaultSpacingPresets.icons['80x80'].h / iconsProportion,
      },
      '92x92': {
        w: defaultSpacingPresets.icons['92x92'].w / iconsProportion,
        h: defaultSpacingPresets.icons['92x92'].h / iconsProportion,
      },
    },
  };
};

// Отступы используемые повсеместно, которые должны быть
// Одинаковыми во всех компонентах и зависят от текущего размера экрана
export const useGetSpacing = ({ currentBp }: { currentBp: Breakpoint }) => {
  const theme = useTheme();

  const [proportions] = useState({
    desktop_large:
      theme.breakpoints.values.desktop_middle /
      theme.breakpoints.values.desktop_large,
    desktop_middle: 1,
    desktop_small:
      theme.breakpoints.values.desktop_middle /
      theme.breakpoints.values.desktop_small,
    laptop:
      theme.breakpoints.values.desktop_middle / theme.breakpoints.values.laptop,
    mobile: theme.breakpoints.values.desktop_small / 700,
  });

  const [largePreset] = useState(
    generateProportionalPreset(proportions['desktop_large'], 1)
  );
  const [smallPreset] = useState(
    generateProportionalPreset(proportions['desktop_small'], 1.1)
  );
  const [laptopPreset] = useState(
    generateProportionalPreset(proportions['laptop'], 1.2)
  );
  const [mobilePreset] = useState(
    generateProportionalPreset(proportions['mobile'], 1.2)
  );

  const [spacingPreset, setSpacingPreset] = useState(defaultSpacingPresets);

  useEffect(() => {
    switch (currentBp) {
      case 'desktop_large':
      case 'desktop_middle':
        setSpacingPreset(defaultSpacingPresets);
        break;
      case 'desktop_small':
        setSpacingPreset(smallPreset);
        break;
      case 'laptop':
      case 'tablet':
        setSpacingPreset(laptopPreset);
        break;
      default:
        setSpacingPreset(mobilePreset);
        break;
    }
  }, [currentBp, theme, laptopPreset, smallPreset, largePreset, mobilePreset]);

  return spacingPreset;
};
